
import { DeliveryProvider, OperatingContext, SystemConfig, Driver, Order } from '../../../types';
import { getOpenDeliveryAvailabilityQuote } from '../../openDeliveryLogisticsService';

export type CanonicalCostScoringInput = {
    ownCost: number;
    thirdCost: number;
    savings: number;
    deviationRatio: number;
    hasMinimumSavings: boolean;
    exceedsDeviationThreshold: boolean;
    internalMargin: number;
    thirdPartyMargin: number;
    hasThirdPartyCost: boolean;
};

type BuildCanonicalCostScoringInputParams = {
    ownCost: number;
    thirdCost: number;
    customerFees: number;
    minimumSavings: number;
    deviationThreshold: number;
};

export const buildCanonicalCostScoringInput = ({
    ownCost,
    thirdCost,
    customerFees,
    minimumSavings,
    deviationThreshold
}: BuildCanonicalCostScoringInputParams): CanonicalCostScoringInput => {
    const savings = ownCost - thirdCost;
    const hasThirdPartyCost = Number.isFinite(thirdCost) && thirdCost > 0;
    const deviationRatio = hasThirdPartyCost ? (ownCost / thirdCost) : 0;
    const hasMinimumSavings = savings >= minimumSavings;
    const exceedsDeviationThreshold = Number.isFinite(deviationThreshold)
        && hasThirdPartyCost
        && deviationRatio >= deviationThreshold;

    return {
        ownCost,
        thirdCost,
        savings,
        deviationRatio,
        hasMinimumSavings,
        exceedsDeviationThreshold,
        internalMargin: customerFees - ownCost,
        thirdPartyMargin: customerFees - thirdCost,
        hasThirdPartyCost
    };
};

const calculateProviderTablePrice = (
    provider: DeliveryProvider,
    distance: number,
    context: OperatingContext,
    config: SystemConfig
): number => {
    let price = 0;

    if (provider.strategy === 'flat') {
        price = provider.baseFee || 0;
    } else if (provider.strategy === 'per_km') {
        price = (provider.baseFee || 0) + ((provider.pricePerKm || 0) * distance);
    } else if (provider.strategy === 'tiered') {
        const tier = (provider.tiers || []).find(t => distance <= t.maxDistance);
        if (tier) {
            price = tier.price;
        } else {
            const lastTier = provider.tiers && provider.tiers.length > 0 ? provider.tiers[provider.tiers.length - 1] : null;
            price = lastTier ? lastTier.price : (provider.baseFee || 0);
        }
    }

    if (context.isHighTraffic) {
        price *= (1 + (config.thirdPartyConfig?.trafficRiskPercentage || 0) / 100);
    }

    if (context.rainMode === 'light') {
        price *= (1 + (config.thirdPartyConfig?.rainSurchargeLight || 0) / 100);
    } else if (context.rainMode === 'heavy') {
        price *= (1 + (config.thirdPartyConfig?.rainSurchargeHeavy || 0) / 100);
    }

    const today = new Date().getDay();
    (provider.conditions || []).forEach(cond => {
        if (cond.active && cond.type === 'day_of_week' && cond.days?.includes(today)) {
            if (cond.modifierType === 'percentage') price *= (1 + (cond.value / 100));
            else price += cond.value;
        }
    });

    return price;
};

/**
 * v45.6: findBestProviderQuote - FULL INTEGRITY
 * Calcula o custo real baseado nas tabelas de parceiros (Flat, Per KM, Tiered)
 */
export const findBestProviderQuote = (
    distance: number,
    providers: DeliveryProvider[],
    context: OperatingContext,
    isManualCost: boolean,
    config: SystemConfig
): { price: number, name: string } => {
    const activeProviders = (providers || []).filter(p => p.active);

    // v57.5: CORREÇÃO CRÍTICA - Não permitir alocação externa sem provider configurado
    if (activeProviders.length === 0) {
        console.warn('[CALCULATOR] Nenhum provider ativo - impedindo alocação externa');
        return { price: Infinity, name: 'Sem Providers' }; // Força frota própria
    }

    let bestPrice = Infinity;
    let bestName = 'Nenhum';

    activeProviders.forEach(p => {
        const price = calculateProviderTablePrice(p, distance, context, config);

        if (price > 0 && price < bestPrice) {
            bestPrice = price;
            bestName = p.name;
        } else if (price === 0) {
            // v57.5: Detectar configuração inválida de provider
            console.warn(`[CALCULATOR] Provider "${p.name}" calculou preço R$ 0,00 - possível configuração inválida`, {
                strategy: p.strategy,
                baseFee: p.baseFee,
                pricePerKm: p.pricePerKm,
                distance
            });
        }
    });

    // v57.5: VALIDAÇÃO - Nunca retornar 0 quando deveria usar terceiros
    if (bestPrice === Infinity || bestPrice === 0) {
        console.error('[CALCULATOR] ERRO CRÍTICO: Nenhum provider gerou preço válido', {
            activeProviders: activeProviders.map(p => ({ name: p.name, strategy: p.strategy, baseFee: p.baseFee, pricePerKm: p.pricePerKm })),
            distance
        });
        // Retorna custo proibitivo para forçar frota própria
        return { price: 9999, name: 'ERRO: Verificar Configuração' };
    }

    return {
        price: bestPrice,
        name: bestName
    };
};

export const findBestProviderQuoteAsync = async (
    distance: number,
    order: Order,
    providers: DeliveryProvider[],
    context: OperatingContext,
    config: SystemConfig,
    origin?: { lat: number; lng: number }
): Promise<{ price: number, name: string, source: 'api' | 'fallback' }> => {
    const activeProviders = (providers || []).filter(p => p.active);

    if (activeProviders.length === 0) {
        return { price: Infinity, name: 'Sem Providers', source: 'fallback' };
    }

    let bestPrice = Infinity;
    let bestName = 'Nenhum';
    let bestSource: 'api' | 'fallback' = 'fallback';

    for (const provider of activeProviders) {
        const fallbackPrice = calculateProviderTablePrice(provider, distance, context, config);
        let candidatePrice = fallbackPrice;
        let source: 'api' | 'fallback' = 'fallback';

        const canUseOpenDeliveryApi = provider.integrationType === 'open_delivery' && !!provider.openDeliveryConfig?.active;

        if (canUseOpenDeliveryApi) {
            try {
                const quote = await getOpenDeliveryAvailabilityQuote({
                    order,
                    provider,
                    config,
                    origin,
                    distanceKm: distance
                });

                if (quote.available && Number.isFinite(quote.price) && quote.price > 0) {
                    candidatePrice = quote.price;
                    source = 'api';
                }
            } catch {
                source = 'fallback';
            }
        }

        if (candidatePrice > 0 && candidatePrice < bestPrice) {
            bestPrice = candidatePrice;
            bestName = provider.name;
            bestSource = source;
        }
    }

    if (!Number.isFinite(bestPrice) || bestPrice <= 0) {
        return { price: 9999, name: 'ERRO: Verificar Configuração', source: 'fallback' };
    }

    return { price: bestPrice, name: bestName, source: bestSource };
};

/**
 * v2025.02.08: calculateInternalCostV2 - REGRAS DE OURO CORRIGIDAS
 * Calcula o custo baseado na Diária, Carga Horária, Taxa Fixa e Tempo de Serviço.
 * Fórmula completa: (tempo_total * custo/min) + (taxa_fixa * qtd) + (tempo_servico * qtd * custo/min)
 */
export const calculateInternalCostV2 = (
    times: { outbound: number, inter: number, return: number },
    isBusy: boolean,
    count: number,
    load: number,
    config: any,
    multiplier: number
) => {
    // Regra Obrigatória: Diária / (Horas * 60)
    const dailyFee = config.fleetConfig?.dailyFixedCost || 0;
    const shiftHours = config.fleetConfig?.averageShiftHours || 8;
    const costPerMinute = dailyFee / (shiftHours * 60);

    const fixedPerOrder = config.fleetConfig?.driverFixedFeePerOrder || 0;
    const serviceTimeMinutes = config.fleetConfig?.serviceTimeMinutes || 5;

    // Tempo Total da Rota = Ida + Entres + Volta
    const totalTravelTime = times.outbound + times.inter + times.return;
    
    // Tempo Total de Serviço = tempo_na_porta * quantidade_de_pedidos
    const totalServiceTime = serviceTimeMinutes * count;

    // Custo de Caixa Completo:
    // = (Tempo Viagem * Custo/Min) + (Tempo Serviço * Custo/Min) + (Taxa Fixa * Qtd Pedidos)
    const travelCost = totalTravelTime * costPerMinute;
    const serviceCost = totalServiceTime * costPerMinute;
    const fixedCost = fixedPerOrder * count;
    
    const cashCost = travelCost + serviceCost + fixedCost;

    return {
        cashCost: cashCost || 0,
        totalDecisionCost: (cashCost || 0) * multiplier
    };
};
